<script setup lang="ts">
import Dialog from 'primevue/dialog';
import { computed } from 'vue';
import { AppSkeletonLoader, AppTypography } from '~/components/base';
import CampaignDetails from '~/components/campaigns/CampaignDetails.vue';
import { useCampaign } from '~/composables/useCampaign';

// The id is fixed for the life of this component: the parent mounts it with
// `v-if` per open, so useCampaign's one-shot fetch runs for the campaign that
// was actually clicked.
const { campaignId } = defineProps<{ campaignId: string }>();

const visible = defineModel<boolean>('visible', { default: false });

const { campaign, pending, error } = useCampaign(campaignId);

const href = computed(() => `/campaigns/${campaignId}`);

// VTW-497: this popup is what the reporter is looking at on desktop, so Back
// from the report page must come back to the listing with it reopened — not to
// the standalone /campaigns/[id] page, which is the mobile presentation.
const reportReturnTo = computed(
  () => `/campaigns?campaign=${encodeURIComponent(campaignId)}`,
);
</script>

<template>
  <Dialog
    v-model:visible="visible"
    :draggable="false"
    modal
    :show-header="false"
    dismissable-mask
    :aria-label="campaign?.title ?? 'Campaign details'"
    data-testid="campaign-detail-modal"
    class="app-modal campaign-detail-modal w-full max-w-[640px]"
  >
    <div v-if="pending" class="flex flex-col gap-3">
      <AppSkeletonLoader :items="[{ height: '32px' }]" container-class="" />
      <AppSkeletonLoader :items="[{ height: '160px' }]" container-class="" />
    </div>

    <!-- Same component the /campaigns/[id] page renders, so the modal is that
         page's content verbatim — type, spacing and colours included. -->
    <CampaignDetails
      v-else-if="campaign"
      :campaign="campaign"
      title-element="h2"
      :report-return-to="reportReturnTo"
    />

    <!-- A failed request and a missing campaign are different things: the first
         is worth retrying, the second isn't. Checked before the not-found
         fallback so a 5xx isn't reported as "doesn't exist". -->
    <div
      v-else-if="error"
      data-testid="campaign-modal-error"
      class="campaign-detail-modal__fallback flex flex-col items-center gap-2 text-center"
    >
      <AppTypography type="body"
        >Couldn&rsquo;t load this campaign</AppTypography
      >
      <NuxtLink :to="href" class="campaign-detail-modal__fallback-link">
        <AppTypography type="footnote-bold" element="span" class="underline">
          Open the full page
        </AppTypography>
      </NuxtLink>
    </div>

    <div
      v-else
      data-testid="campaign-modal-not-found"
      class="campaign-detail-modal__fallback text-center"
    >
      <AppTypography type="body">Campaign not found</AppTypography>
    </div>
  </Dialog>
</template>

<style scoped lang="scss">
.campaign-detail-modal {
  &__fallback {
    padding: $spacing-2xl 0;
  }

  &__fallback-link {
    color: $text-color-brand;
  }
}
</style>

<!--
  Unscoped: the dialog is teleported to <body> without this component's scoped
  attribute, so its chrome can only be reached by the class the panel carries.
  The padding and radius mirror the page's `__panel`, which is the surface this
  content sits on there. Kept narrow to this modal.
-->
<style lang="scss">
.campaign-detail-modal.p-dialog {
  border-radius: $radius-2xl;
  background-color: $surface-color-bg;
  // PrimeVue's dialog theme sets its own slate text colour on the panel, which
  // tints everything that inherits (the title, the Sources row). On the page
  // these fall back to the document default, so reset to that — `inherit` is no
  // good here, the teleported panel's parent is the dialog mask, which is also
  // themed.
  color: initial;

  .p-dialog-content {
    padding: $spacing-2xl;
    border-radius: $radius-2xl;
    background-color: $surface-color-bg;
    color: initial;
  }
}
</style>
